import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { Trash2, BellRing, BellPlus } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

export default function SavedSearchesPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/saved-searches')
      .then((r) => setSearches(r.data))
      .catch((e) => toast.error(getApiErrorMessage(e, t('savedSearch.loadFailed'))))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      setSearches((prev) => prev.filter((s) => s.id !== id));
      toast.success(t('savedSearch.removed'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('savedSearch.removeFailed')));
    }
  };

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('savedSearch.title')}</h1>
        <Button onClick={() => navigate('/search?tab=trip')} variant="default" size="sm">
          <BellPlus size={14} className="mr-1"/> {t('savedSearch.createAlert')}
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">{t('savedSearch.active')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-muted-foreground">{t('savedSearch.loading')}</p>}
          {!loading && searches.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('savedSearch.empty')}
            </p>
          )}
          {!loading && searches.map((s) => (
            <div key={s.id} className="flex items-center justify-between border-b last:border-0 pb-3 last:pb-0">
              <div className="flex items-center gap-2 text-sm">
                <BellRing size={14} className="text-primary" />
                <span className="font-medium">{s.kind === 'deliveries' ? t('savedSearch.parcels') : t('savedSearch.trips')}</span>
                <span className="text-muted-foreground">
                  {s.departure_city || t('savedSearch.anyCity')} → {s.arrival_city || t('savedSearch.anyCity')}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(s.id)}
                title={t('savedSearch.remove')}
                aria-label={t('savedSearch.remove')}
              ><Trash2 size={14} /></Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </DashboardShell>
  );
}
